import { useState, useEffect } from "react";
import { useNavigate, Outlet, NavLink, useLocation, Navigate } from "react-router-dom";
import { assetUrl } from "fusion-core";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { toast } from "sonner";
import {
  IconChevronsLeft,
  IconFolder,
  IconLayers,
  IconLayoutDashboard,
  IconLogOut,
  IconMenu,
  IconPackage,
  IconShield,
  IconUsers,
  IconX,
} from "./icons";
import { clearStoredToken, isStoredTokenValid } from "./authStorage";

const NAV_ITEMS = [
  {
    to: "/admin/dashboard",
    end: true,
    label: "Overview",
    icon: IconLayoutDashboard,
  },
  { to: "/admin/dashboard/users", label: "Users", icon: IconUsers },
  { to: "/admin/dashboard/categories", label: "Categories", icon: IconFolder },
  {
    to: "/admin/dashboard/scheme-features",
    label: "Scheme Features",
    icon: IconLayers,
  },
  { to: "/admin/dashboard/addons", label: "Addons", icon: IconPackage },
];

function getPageMeta(pathname) {
  if (pathname.includes("/scheme-features/edit")) {
    return { title: "Edit Scheme Feature", icon: IconLayers };
  }
  if (pathname.includes("/scheme-features/new")) {
    return { title: "New Scheme Feature", icon: IconLayers };
  }

  const match = NAV_ITEMS.find(
    (item) =>
      item.to === pathname || (!item.end && pathname.startsWith(`${item.to}/`)),
  );

  if (match) {
    return { title: match.label, icon: match.icon };
  }

  return { title: "Overview", icon: IconLayoutDashboard };
}

export default function Dashboard() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pageSubtitle, setPageSubtitle] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsSidebarCollapsed(mobile);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageSubtitle(null);
  }, [location.pathname]);

  const handleLogout = () => {
    clearStoredToken();
    toast.success("Logged out successfully");
    navigate("/admin");
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  };

  const { title: pageTitle } = getPageMeta(location.pathname);
  const isMobileNavOpen = isMobile && !isSidebarCollapsed;

  if (!isStoredTokenValid()) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <>
      <Helmet>
        <link rel="stylesheet" href={assetUrl("./styles/Dashboard.css")} />
        <link rel="stylesheet" href={assetUrl("./styles/UserManagement.css")} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Helmet>

      <div
        className={`admin-shell ${
          isSidebarCollapsed ? "admin-shell--collapsed" : ""
        } ${isMobile ? "admin-shell--mobile" : ""}`}
      >
        {isMobileNavOpen && (
          <button
            type="button"
            className="admin-shell__backdrop"
            onClick={closeMobileSidebar}
            aria-label="Close navigation"
          />
        )}

        <aside
          className={`admin-sidebar ${
            isSidebarCollapsed ? "admin-sidebar--collapsed" : ""
          } ${isMobileNavOpen ? "admin-sidebar--open" : ""}`}
        >
          <div className="admin-sidebar__head">
            <div className="admin-sidebar__brand">
              <span className="admin-sidebar__brand-icon" aria-hidden="true">
                <IconShield size={18} />
              </span>
              <div className="admin-sidebar__brand-text">
                <span className="admin-sidebar__brand-title">Admin</span>
                <span className="admin-sidebar__brand-sub">Portal</span>
              </div>
            </div>
            <button
              type="button"
              className="admin-sidebar__toggle"
              onClick={toggleSidebar}
              title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={
                isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
            >
              <IconChevronsLeft size={18} />
            </button>
          </div>

          <nav className="admin-sidebar__nav">
            <p className="admin-sidebar__section">Main menu</p>
            <ul>
              {NAV_ITEMS.map(({ to, end, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    title={label}
                    className={({ isActive }) =>
                      `admin-sidebar__link${
                        isActive ? " admin-sidebar__link--active" : ""
                      }`
                    }
                    onClick={closeMobileSidebar}
                  >
                    <span className="admin-sidebar__link-icon">
                      <Icon size={18} />
                    </span>
                    <span className="admin-sidebar__link-label">{label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="admin-sidebar__user">
            <span className="admin-sidebar__avatar" aria-hidden="true">
              AD
            </span>
            <div className="admin-sidebar__user-info">
              <span className="admin-sidebar__user-name">Admin</span>
              <span className="admin-sidebar__user-email">Administrator</span>
            </div>
            <button
              type="button"
              className="admin-sidebar__logout"
              onClick={handleLogout}
              title="Log out"
              aria-label="Log out"
            >
              <IconLogOut size={18} />
            </button>
          </div>
        </aside>

        <div className="admin-shell__main">
          <header className="admin-topbar">
            <div className="admin-topbar__left">
              <button
                type="button"
                className="admin-topbar__menu"
                onClick={toggleSidebar}
                aria-label="Toggle navigation"
              >
                {isMobileNavOpen ? <IconX size={20} /> : <IconMenu size={20} />}
              </button>
              <div className="admin-topbar__heading">
                <h1 className="admin-topbar__title">{pageTitle}</h1>
                {pageSubtitle ? (
                  <p className="admin-topbar__subtitle">{pageSubtitle}</p>
                ) : null}
              </div>
            </div>
          </header>

          <main className="admin-page">
            <Outlet context={{ setPageSubtitle }} />
          </main>
        </div>
      </div>
    </>
  );
}
